import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { organizationService, settingsService, notificationService } from '../services/api';
import { Organization, Notification, FREEMIUM_LIMITS } from '../types';
import { usePaystackPayment } from 'react-paystack';
import { toast } from 'react-hot-toast';
import { subscriptionService, SubscriptionUsage } from '../services/subscriptionService';

export const TenantBillingPage: React.FC = () => {
  const { user } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [bankDetails, setBankDetails] = useState<any>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [usage, setUsage] = useState<SubscriptionUsage | null>(null);
  const [loading, setLoading] = useState(true);

  const isOrg = Boolean(user?.organizationId);
  const priceNgn = isOrg ? 50000 : 5000;
  const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_live_17b9284e8e4e4333a1ab0479130f01a63ac29a60';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const usageData = await subscriptionService.getUsageAndLimits(user);
      setUsage(usageData);

      const [bankRes, notifRes] = await Promise.all([
        settingsService.getBankDetails().catch(() => ({ data: null })),
        user?.organizationId
          ? notificationService.getNotificationsForOrg(user.organizationId).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);

      setBankDetails(bankRes.data);
      setNotifications(
        (notifRes.data || []).sort(
          (a: any, b: any) => b.createdAt?.toMillis?.() - a.createdAt?.toMillis?.()
        )
      );

      if (user?.organizationId) {
        const orgRes = await organizationService.getOrganization(user.organizationId);
        setOrganization(orgRes.data);
      }
    } catch (err) {
      console.warn('Billing data loading notice:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const markAsRead = async (notifId: string) => {
    try {
      await notificationService.markAsRead(notifId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, isRead: true } : n))
      );
    } catch (err) {
      console.error(err);
    }
  };

  // Paystack Config
  const paystackConfig = {
    reference: `VRS-SUB-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    email: user?.email || 'billing@verisure.online',
    amount: priceNgn * 100, // in kobo
    publicKey: PAYSTACK_PUBLIC_KEY,
  };

  const initializePayment = usePaystackPayment(paystackConfig);

  const onSuccess = async (reference: any) => {
    toast.success('Payment verified! Activating Premium access...');
    try {
      if (isOrg && user?.organizationId) {
        await subscriptionService.upgradeOrgSubscription(user.organizationId, 'premium');
      } else if (user?.id) {
        await subscriptionService.upgradeUserSubscription(user.id, 'premium');
      }
      toast.success('🎉 Premium Subscription Active! Unlimited QR Codes unlocked.');
      loadData();
    } catch (error) {
      toast.error('Failed to update subscription status');
    }
  };

  const onClose = () => {
    toast('Payment cancelled.', { icon: 'ℹ️' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-8 text-orange-400 font-bold">
        <svg className="animate-spin h-8 w-8 text-orange-500 mb-3" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        <span>Loading Subscription &amp; Billing Data...</span>
      </div>
    );
  }

  const isPremium = usage?.tier === 'premium' && usage?.status === 'active';
  const currentCount = usage?.currentCount || 0;
  const limit = usage?.limit || (isOrg ? FREEMIUM_LIMITS.ORGANIZATION : FREEMIUM_LIMITS.INDIVIDUAL);
  const percentageUsed = isPremium ? 0 : Math.min(100, Math.round((currentCount / limit) * 100));

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-3 sm:p-6 md:p-8 min-w-0 max-w-full overflow-hidden font-sans">
      <div className="max-w-6xl mx-auto space-y-6 min-w-0 max-w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-500/15 border border-orange-500/30 text-orange-400 text-[11px] font-black rounded-full uppercase tracking-wider mb-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span>VERISURE FREEMIUM SUBSCRIPTION ENGINE</span>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white flex items-center gap-3">
              <span>Billing &amp; Subscriptions</span>
              <button
                onClick={loadData}
                className="p-1.5 sm:p-2 bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white rounded-full transition-colors border border-white/10"
                title="Refresh Subscription Status"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </h1>
            <p className="text-slate-400 text-xs sm:text-sm mt-1">
              {isOrg
                ? `Manage organization billing, payments, and QR code quota for ${organization?.companyName || 'your team'}.`
                : 'Manage your individual plan, QR code allowances, and online payment methods.'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Plan Card */}
          <div className="lg:col-span-2 glass-card rounded-3xl p-6 sm:p-8 border border-white/[0.08] space-y-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-red-600 via-orange-500 to-amber-500"></div>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <h2 className="text-xl font-black text-white">Current Membership Tier</h2>
                <p className="text-xs text-slate-400">
                  {isOrg ? 'Organization Account Status' : 'Individual Equipment Owner Account'}
                </p>
              </div>
              <span
                className={`px-3.5 py-1 rounded-full text-xs font-black uppercase tracking-wider ${
                  isPremium
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    : 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                }`}
              >
                {isPremium ? '★ PREMIUM ACTIVE' : 'FREE STARTER PLAN'}
              </span>
            </div>

            {/* Plan Details Box */}
            <div className="p-5 bg-slate-900/80 rounded-2xl border border-white/[0.08] flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-2xl shadow-lg shadow-orange-500/20 shrink-0">
                {isPremium ? '👑' : '🎁'}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-black text-white">
                  {isPremium
                    ? (isOrg ? 'Enterprise Premium Tier' : 'Individual Pro Tier')
                    : (isOrg ? 'Organization Free Starter Tier' : 'Individual Free Plan')}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                  {isPremium
                    ? 'You have complete, unrestricted access to unlimited QR codes, high-speed batch printing, and tamper forensics.'
                    : isOrg
                    ? 'You can register and print up to 10 QR codes completely free of charge. Upgrade to Premium for unlimited codes.'
                    : 'You can register and protect up to 5 devices completely free of charge. Upgrade to Pro for unlimited codes.'}
                </p>
              </div>
            </div>

            {/* Freemium Usage Meter */}
            <div className="p-5 bg-[#090d16] rounded-2xl border border-white/[0.08] space-y-2">
              <div className="flex justify-between items-center text-xs font-bold">
                <span className="text-slate-400">Hardware QR Code Quota</span>
                <span className="font-mono text-orange-400">
                  {isPremium ? 'Unlimited / ∞' : `${currentCount} of ${limit} Used (${percentageUsed}%)`}
                </span>
              </div>
              <div className="w-full bg-slate-800 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${
                    percentageUsed >= 100
                      ? 'bg-red-500'
                      : percentageUsed >= 70
                      ? 'bg-amber-500'
                      : 'bg-gradient-to-r from-orange-500 to-amber-500'
                  }`}
                  style={{ width: isPremium ? '100%' : `${percentageUsed}%` }}
                ></div>
              </div>
              <p className="text-[11px] text-slate-500">
                {isPremium
                  ? 'Unlimited QR identity generation is enabled on your active subscription.'
                  : `You have ${Math.max(0, limit - currentCount)} free QR code slots remaining.`}
              </p>
            </div>

            {/* Upgrade & Payment Actions */}
            {!isPremium && (
              <div className="space-y-4 pt-4 border-t border-white/[0.08]">
                <div>
                  <h3 className="text-base font-black text-white">
                    Upgrade to Premium (₦{priceNgn.toLocaleString()}/month)
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Select your preferred payment method below to remove the {limit} QR code cap immediately.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Paystack Online Payment */}
                  <div className="bg-slate-900/80 p-5 rounded-2xl border border-white/[0.08] flex flex-col justify-between hover:border-orange-500/40 transition">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">💳</span>
                        <h4 className="font-black text-sm text-white">Instant Card / Paystack</h4>
                      </div>
                      <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                        Pay with debit/credit card, USSD, or Bank Account. Instant automated activation within seconds.
                      </p>
                    </div>
                    <button
                      onClick={() => initializePayment({ onSuccess, onClose })}
                      className="w-full py-3 bg-[#0ba4db] hover:bg-[#098bbd] text-white font-black text-xs rounded-xl transition shadow-md shadow-[#0ba4db]/20 flex items-center justify-center gap-2"
                    >
                      <span>Pay ₦{priceNgn.toLocaleString()} Online</span>
                      <span>&rarr;</span>
                    </button>
                  </div>

                  {/* Bank Transfer Box */}
                  <div className="bg-slate-900/80 p-5 rounded-2xl border border-white/[0.08] flex flex-col justify-between hover:border-emerald-500/40 transition">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">🏦</span>
                        <h4 className="font-black text-sm text-white">Direct Bank Transfer</h4>
                      </div>
                      {bankDetails ? (
                        <div className="text-xs text-slate-300 space-y-1 mb-4 bg-slate-950 p-3 rounded-xl border border-white/5 font-mono">
                          <p><span className="text-slate-500 font-sans">Bank:</span> {bankDetails.bankName}</p>
                          <p><span className="text-slate-500 font-sans">Account:</span> <span className="font-bold text-orange-400">{bankDetails.accountNumber}</span></p>
                          <p><span className="text-slate-500 font-sans">Name:</span> {bankDetails.accountName}</p>
                        </div>
                      ) : (
                        <div className="text-xs text-slate-300 space-y-1 mb-4 bg-slate-950 p-3 rounded-xl border border-white/5 font-mono">
                          <p><span className="text-slate-500 font-sans">Bank:</span> Access Bank Plc</p>
                          <p><span className="text-slate-500 font-sans">Account:</span> <span className="font-bold text-orange-400">0129384756</span></p>
                          <p><span className="text-slate-500 font-sans">Name:</span> Elemental Verisure Tech Ltd</p>
                        </div>
                      )}
                    </div>
                    <p className="text-[11px] text-center text-slate-400 font-semibold bg-slate-950 py-2 rounded-xl border border-white/5">
                      Send payment proof to billing@verisure.online
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notices & Notifications Sidebar */}
          <div className="glass-card rounded-3xl p-6 border border-white/[0.08] flex flex-col h-full max-h-[620px]">
            <h2 className="text-lg font-black text-white mb-1">Billing Notices &amp; Ledger</h2>
            <p className="text-xs text-slate-400 mb-4">Official platform communications</p>

            <div className="flex-1 overflow-y-auto pr-1 space-y-3">
              {notifications.length === 0 ? (
                <div className="text-center text-slate-500 mt-12 text-xs">
                  <div className="text-2xl mb-2">📬</div>
                  No billing alerts or default notices on file.
                </div>
              ) : (
                notifications.map((n) => (
                  <div
                    key={n.id}
                    className={`p-3.5 rounded-2xl border text-xs space-y-1 relative ${
                      n.type === 'warning'
                        ? 'bg-amber-500/10 border-amber-500/30 text-amber-200'
                        : n.type === 'payment_default'
                        ? 'bg-red-500/10 border-red-500/30 text-red-200'
                        : 'bg-slate-900 border-white/10 text-slate-300'
                    }`}
                  >
                    {!n.isRead && (
                      <span className="absolute top-2 right-2 w-2 h-2 bg-orange-400 rounded-full animate-ping"></span>
                    )}
                    <h4 className="font-bold text-white text-xs">{n.title}</h4>
                    <p className="text-[11px] text-slate-400">{n.message}</p>
                    <div className="mt-2 flex justify-between items-center text-[10px] text-slate-500">
                      <span>{n.createdAt?.toDate ? n.createdAt.toDate().toLocaleDateString() : 'Recent'}</span>
                      {!n.isRead && (
                        <button
                          onClick={() => markAsRead(n.id!)}
                          className="text-orange-400 hover:underline font-bold"
                        >
                          Mark Read
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
